import React, { createContext, useState, useEffect, useRef } from 'react';
import API from '../api/axios';
import { identifySessionUser } from '../utils/sessionReplay';

export const AuthContext = createContext();

// Helper: decode a JWT in the browser. Uses native atob — NOT Buffer, which is
// not available in the browser bundle (CRA/webpack 5 doesn't polyfill it). Using
// Buffer here silently returned null, so the token's real `exp` was never read
// and the session fell back to a 7-day expiry instead of the tenant's setting.
function decodeJwt(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    return JSON.parse(atob(b64));
  } catch (e) {
    return null;
  }
}

// Decode JWT payload using native atob (no Buffer polyfill dependency).
function simpleDecodeJwt(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    return JSON.parse(atob(b64));
  } catch (e) {
    return null;
  }
}

// Run BEFORE React renders to capture impersonation token from URL hash.
// This ensures localStorage is populated before the first component render.
(function processImpersonationHash() {
  try {
    const hash = window.location.hash;
    if (!hash || !hash.includes('access_token=')) return;
    const params = new URLSearchParams(hash.substring(1));
    const accessToken = params.get('access_token');
    const impersonating = params.get('impersonating');
    if (!accessToken) return;
    const payload = simpleDecodeJwt(accessToken);
    if (payload) {
      localStorage.setItem('token', accessToken);
      localStorage.setItem('user', JSON.stringify({
        user_id: payload.user_id,
        name: payload.name,
        email: payload.email,
        role: payload.role,
        college_id: payload.college_id || null,
        university_id: payload.university_id || null,
        department_id: payload.department_id || null,
        subject_id: payload.subject_id || null,
        department: payload.department || null
      }));
      if (impersonating === 'true') {
        localStorage.setItem('is_impersonating', 'true');
      }
    }
    window.history.replaceState(null, '', window.location.pathname);
  } catch (_) {}
})();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutTimer = useRef(null);

  const scheduleLogout = (expiryMs) => {
    if (logoutTimer.current) {
      clearTimeout(logoutTimer.current);
      logoutTimer.current = null;
    }

    const now = Date.now();
    const ms = expiryMs - now;
    if (ms > 0) {
      logoutTimer.current = setTimeout(() => {
        logout();
      }, ms);
    } else {
      logout();
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const tokenExpiry = localStorage.getItem('tokenExpiry');

    if (token && userData) {
      // If tokenExpiry exists, check it. Otherwise try to decode token's exp claim.
      let expiryMs = tokenExpiry ? parseInt(tokenExpiry, 10) : null;
      if (!expiryMs) {
        const payload = decodeJwt(token);
        if (payload && payload.exp) {
          expiryMs = payload.exp * 1000;
        }
      }

      if (expiryMs) {
        if (Date.now() >= expiryMs) {
          // expired
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('tokenExpiry');
          setUser(null);
        } else {
          setUser(JSON.parse(userData));
          scheduleLogout(expiryMs);
        }
      } else {
        // No expiry info; set user but schedule a fallback logout in 7 days
        const fallback = Date.now() + 7 * 24 * 60 * 60 * 1000;
        localStorage.setItem('tokenExpiry', String(fallback));
        setUser(JSON.parse(userData));
        scheduleLogout(fallback);
      }
    }

    setLoading(false);

    return () => {
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    identifySessionUser(user);
  }, [user]);

  const setTokenWithExpiry = (token) => {
    localStorage.setItem('token', token);
    const payload = decodeJwt(token);
    let expiryMs;
    if (payload && payload.exp) {
      expiryMs = payload.exp * 1000;
    } else {
      // fallback to 7 days
      expiryMs = Date.now() + 7 * 24 * 60 * 60 * 1000;
    }
    localStorage.setItem('tokenExpiry', String(expiryMs));
    scheduleLogout(expiryMs);
  };

  const login = async (email, password, twoFactorCode = '') => {
    const { data } = await API.post('/auth/login', { email, password, twoFactorCode });

    if (data.success && data.token) {
      setTokenWithExpiry(data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
    }

    return data;
  };

  const register = async (formData) => {
    const { data } = await API.post('/auth/register', formData);
    if (data.token) {
      setTokenWithExpiry(data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);
    }
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('is_impersonating');
    localStorage.removeItem('admin_access_token_backup');
    localStorage.removeItem('admin_user_backup');
    localStorage.removeItem('impersonation_target');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
